import { calcSalaryFull, getEmployeeMonthStatus } from './salaryCalculator';
import { DEFAULT_CONFIG } from './payrollConstants';

// Overview, charts, and comparisons deliberately use the same calculation basis.
export function summarizeDashboardMonth({ employees, salaries, config = DEFAULT_CONFIG, month, year }) {
  let summAbs = 0, summOtH = 0, summOtP = 0, summPfElig = 0, summEsiElig = 0;
  let summGross = 0, summNet = 0, summEmpPf = 0, summEmpEsi = 0, summGratuity = 0;
  let summErPf = 0, summErEsi = 0, summPfWage = 0, summAdmin = 0, summEdli = 0;
  let otViolators = [], noPfList = [], noEsiList = [], epsExemptedList = [];
  let onboarded = [], exited = [];
  const employeeRows = [];
  const components = { basic: 0, hra: 0, allowances: 0, bonus: 0 };
  let savedCount = 0;
  
  const wp = config?.working_days === -1 ? new Date(year, month, 0).getDate() : (config?.working_days || 26);
  
  employees.forEach(e => {
    const status = getEmployeeMonthStatus(e, month, year, wp);
    if (!status.eligible) return; // exclude if LWD was before this month

    // Track Onboarded (DOJ is in this month)
    if (e.doj) {
      const dj = new Date(e.doj);
      if (!isNaN(dj) && dj.getFullYear()===year && (dj.getMonth()+1)===month) {
        onboarded.push(e);
      }
    }
    // Track Exited (LWD is in this month)
    if (e.date_of_exit) {
      const de = new Date(e.date_of_exit);
      if (!isNaN(de) && de.getFullYear()===year && (de.getMonth()+1)===month) {
        exited.push(e);
      }
    }

    const s = (salaries[e.id] || []).find(x => x.month === month && x.year === year) || {};
    if (s.id) savedCount++;
    const grossR = e.basic_salary || e.basic || 0;
    const calc = calcSalaryFull({
      basic: grossR, month, year,
      absent_days: (s.absent_days || 0) + status.lwd_absent, ot_hours: s.ot_hours || 0,
      cfg: config,
      extra: { vpf_enabled: (e.vpf_pct||0)>0, vpf_pct: e.vpf_pct||0, tds_amount: e.tds_amount||0, emp_dob: e.date_of_birth, custom_hra_pct: e.custom_hra_pct, esi_forced_until: e.esi_forced_until, custom_special_allowance: e.custom_special_allowance }
    });
    
    components.basic += calc.basic || 0;
    components.hra += calc.hra || 0;
    components.allowances += (calc.da || 0) + (calc.lta || 0) + (calc.special || 0) + (calc.conveyance || 0) + (calc.medical || 0);
    components.bonus += calc.bonus || 0;
    employeeRows.push({
      employee: e,
      absent: (s.absent_days || 0) + status.lwd_absent,
      otHours: s.ot_hours || 0,
      otPay: calc.ot_pay || 0,
      pf: calc.pf_emp || 0,
      esi: calc.esi_emp || 0,
      joined: onboarded.includes(e),
      exited: exited.includes(e),
    });

    summAbs += (s.absent_days || 0) + status.lwd_absent;
    summOtH += (s.ot_hours || 0);
    summOtP += (calc.ot_pay || 0);
    if (calc.pf_emp > 0) summPfElig++; else noPfList.push({ id: e.id, name: e.name, reason: config?.pf_applicable ? (calc.gross <= 0 ? "Zero Salary" : "Exempt / Opted out") : "PF Not Applicable in Company Config" });
    if (calc.esi_emp > 0) summEsiElig++; else noEsiList.push({ id: e.id, name: e.name, reason: config?.esi_applicable ? ((calc.basic + calc.da > 21000) ? "Basic+DA > ₹21,000 (Exempt)" : "Zero Salary") : "ESI Not Applicable in Company Config" });
    if (calc.pf_emp > 0 && calc.eps === 0) epsExemptedList.push(e);
    
    summGross += (calc.gross || 0);
    summNet += (calc.net || 0);
    summGratuity += (calc.gratuity || 0);
    
    const pfw = calc.pfWage || 0;
    const pw = Math.min(pfw, 25000);
    summPfWage += pfw;
    summAdmin += Math.round(pw * 0.005);
    summEdli += Math.round(pw * 0.005);
    
    summEmpPf += (calc.pf_emp || 0) + (calc.vpf || 0);
    summEmpEsi += (calc.esi_emp || 0);
    summErPf += (calc.epf_er || 0) + (calc.eps || 0);
    summErEsi += (calc.esi_er || 0);

    const present = wp - ((s.absent_days || 0) + status.lwd_absent);
    const totalEq = present + ((s.ot_hours || 0) / 8);
    if (totalEq > 31) otViolators.push({ id: e.id, name: e.name || "Unknown", days: totalEq });
  });

  const finalAdmin = (config?.pf_applicable !== false && employeeRows.length > 0) ? (summPfWage > 0 ? Math.max(summAdmin, 500) : 75) : 0;
  const totalErPfExpenditure = summErPf + finalAdmin + summEdli;
  const totalExpenditure = summGross + totalErPfExpenditure + summErEsi + summGratuity;

  return { month, year, summAbs, summOtH, summOtP, summPfElig, summEsiElig, summGross, summNet, summEmpPf, summEmpEsi, summGratuity, summErPf, summErEsi, summPfWage, summAdmin, summEdli, otViolators, noPfList, noEsiList, epsExemptedList, onboarded, exited, employeeRows, finalAdmin, totalErPfExpenditure, totalExpenditure, components, savedCount };
}

export const dashboardMonthKey = ({ month, year }) => `${year}-${month}`;
export function previousDashboardMonth({ month, year }) {
  return month === 1 ? { month: 12, year: year - 1 } : { month: month - 1, year };
}
export function dashboardChange(current, previous) {
  const difference = current - previous;
  return { difference, percent: previous === 0 ? null : difference / Math.abs(previous) * 100 };
}
